import {
  MouseEvent,
  PointerEvent,
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { m, useIsPresent, useReducedMotion } from 'framer-motion';
// @mui
import { Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import NorthEastRounded from '@mui/icons-material/NorthEastRounded';
//
import { YEAR } from '../../../config-global';
import { Product } from '../../../data/products';
import { CONTAINER, FONT, STAGE, TYPE } from '../../../theme/stage';
import { MENU, NEXT_PRODUCT, copyright, ordinal, productNumber } from '../copy';
import { CSS_EASE, DURATION } from '../motion';
import BisqueRing from '../primitives/BisqueRing';
import { ariaCurrentFor } from './nav';

// ----------------------------------------------------------------------

type Props = {
  products: Product[];
  pathname: string;
  active: string | null;
  onNavigate: (href: string, event: MouseEvent) => void;
};

const OUT_EXPO: [number, number, number, number] = [0.16, 1, 0.3, 1];

const STAGGER = 0.07;

const DOT_SPRING = { type: 'spring', stiffness: 450, damping: 42 } as const;

const DOT_FADE = { duration: 0.18, ease: 'easeOut' } as const;

const DOT_APPEAR = { opacity: 0, scale: 0.4 };

const DOT_ID = `${MENU.id}-dot`;

const PRIMARY_HREFS = new Set(MENU.primary.map(({ href }) => href));

const glides = (from?: string, to?: string) =>
  Boolean(from && to && PRIMARY_HREFS.has(from) && PRIMARY_HREFS.has(to));

const SECONDARY = {
  duration: 0.4,
  ease: 'easeOut',
  delay: (MENU.primary.length - 1) * STAGGER + 0.12,
} as const;

const LABEL_ID = { products: `${MENU.id}-products`, legal: `${MENU.id}-legal` };

const HOVER = '@media (hover: hover)';

const COLOR = `color ${DURATION.hover}ms ${CSS_EASE.outExpo}`;

const LIST: SystemStyleObject<Theme> = { m: 0, p: 0, listStyle: 'none' };

const PRIMARY_LINK: SystemStyleObject<Theme> = {
  display: 'flex',
  alignItems: 'baseline',
  gap: { xs: '10px', md: '16px' },
  width: 'fit-content',
  maxWidth: '100%',
  py: '8px',
  borderRadius: '8px',
  fontFamily: FONT.sans,
  fontWeight: 500,
  fontSize: { xs: 40, md: 'clamp(40px, 5vw, 72px)' },
  lineHeight: 1.05,
  letterSpacing: '-0.035em',
  color: STAGE.ink,
  textDecoration: 'none',
  transition: COLOR,
};

const BLOCK_LABEL: SystemStyleObject<Theme> = { ...TYPE.label, m: '0 0 12px', color: STAGE.mute };

const ROW: SystemStyleObject<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: { xs: '12px', sm: '16px' },
  minHeight: 56,
  borderBottom: `1px solid ${STAGE.line}`,
};

const NUMBER: SystemStyleObject<Theme> = {
  ...TYPE.label,
  flexShrink: 0,
  color: STAGE.mute,
  fontVariantNumeric: 'tabular-nums',
};

const NAME: SystemStyleObject<Theme> = {
  flex: '1 0 auto',
  fontFamily: FONT.sans,
  fontWeight: 500,
  fontSize: { xs: 17, sm: 19 },
  lineHeight: 1.2,
  letterSpacing: '-0.01em',
};

const PRODUCT_LINK: SystemStyleObject<Theme> = {
  ...ROW,
  color: STAGE.ink,
  textDecoration: 'none',
  '& .MenuPanel-host': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
    overflow: 'hidden',
    fontFamily: FONT.sans,
    fontSize: 14,
    lineHeight: 1,
    color: STAGE.mute,
    whiteSpace: 'nowrap',
    transition: COLOR,
  },
  '& .MenuPanel-icon': {
    flexShrink: 0,
    fontSize: 16,
    transition: `transform ${DURATION.hover}ms ${CSS_EASE.outExpo}`,
  },
  [HOVER]: { '&:hover .MenuPanel-host': { color: STAGE.ink } },
  '@media (hover: hover) and (prefers-reduced-motion: no-preference)': {
    '&:hover .MenuPanel-icon': { transform: 'translate(2px, -2px)' },
  },
};

const LEGAL_LINK: SystemStyleObject<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  minHeight: 44,
  fontFamily: FONT.sans,
  fontWeight: 500,
  fontSize: 17,
  lineHeight: 1,
  color: STAGE.ink2,
  textDecoration: 'none',
  transition: COLOR,
  '&[aria-current]': { color: STAGE.ink },
  [HOVER]: { '&:hover': { color: STAGE.ink } },
};

const SLOT: SystemStyleObject<Theme> = { alignSelf: 'center', flexShrink: 0, width: 6, height: 6 };

const DOT: SystemStyleObject<Theme> = {
  display: 'block',
  width: 6,
  height: 6,
  border: `3px solid ${STAGE.bisque}`,
  borderRadius: '50%',
};

function Dot({ shown, id, appear }: { shown: boolean; id?: string; appear: boolean }) {
  const reduced = useReducedMotion();

  return (
    <Box component="span" aria-hidden sx={SLOT}>
      {shown && (
        <Box
          key={id}
          component={m.span}
          layoutId={id}
          layoutDependency={DOT_ID}
          initial={appear && !reduced ? DOT_APPEAR : false}
          animate={{ opacity: 1, scale: 1 }}
          transition={
            reduced ? { type: false } : { default: DOT_SPRING, opacity: DOT_FADE, scale: DOT_FADE }
          }
          sx={DOT}
        />
      )}
    </Box>
  );
}

function Secondary({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();

  return (
    <m.div
      initial={reduced ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={SECONDARY}
    >
      {children}
    </m.div>
  );
}

const MenuPanel = forwardRef<HTMLDivElement, Props>(function MenuPanel(
  { products, pathname, active, onNavigate },
  ref
) {
  const reduced = useReducedMotion();

  const present = useIsPresent();

  const next = products[products.length - 1].index + 1;

  const [preview, setPreview] = useState<string | null>(null);

  const tabbing = useRef(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      tabbing.current = event.key === 'Tab';
    };
    const onPointerDown = () => {
      tabbing.current = false;
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, []);

  const [session, setSession] = useState({ present, round: 0 });

  if (session.present !== present) {
    setSession({ present, round: present ? session.round + 1 : session.round });
    if (present) {
      setPreview(null);
    }
  }

  const resting = [...MENU.primary, ...MENU.legal].find((link) =>
    ariaCurrentFor(link, pathname, active)
  )?.href;

  const marked = preview ?? resting;

  const [trail, setTrail] = useState({ marked, round: session.round, epoch: 0, appear: false });

  if (trail.marked !== marked || trail.round !== session.round) {
    const glide = glides(trail.marked, marked);
    setTrail({
      marked,
      round: session.round,
      epoch: glide ? trail.epoch : (trail.epoch + 1) % 3,
      appear: !glide && trail.round === session.round,
    });
  }

  const laneId = `${DOT_ID}-${session.round}-${trail.epoch}`;

  const point = (event: PointerEvent, href: string | null) => {
    if (present && event.pointerType !== 'touch') {
      setPreview(href);
    }
  };

  const tab = (href: string) => {
    if (present && tabbing.current) {
      setPreview(href);
    }
  };

  const leave = (href: string) => {
    if (present) {
      setPreview((value) => (value === href ? null : value));
    }
  };

  return (
    <m.div
      ref={ref}
      id={MENU.id}
      role="dialog"
      aria-modal="true"
      aria-label={MENU.label}
      tabIndex={-1}
      initial={reduced ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: reduced ? 0 : 0.14, ease: 'easeIn' } }}
      transition={{ duration: reduced ? 0 : 0.2, ease: 'easeOut' }}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: STAGE.ground,
        outline: 'none',
        pointerEvents: present ? undefined : 'none',
      }}
    >
      <Box
        component={m.div}
        layoutScroll
        sx={{
          flex: 1,
          minHeight: 0,
          mt: { xs: '56px', sm: '64px' },
          overflowY: 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        <Box sx={{ ...CONTAINER, display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
          <Box
            sx={{
              flexGrow: 1,
              display: 'grid',
              gridTemplateColumns: { md: 'minmax(0, 7fr) minmax(0, 5fr)' },
              alignContent: 'start',
              columnGap: { md: '64px', lg: '96px' },
              rowGap: { xs: '48px', sm: '56px' },
              py: { xs: '32px', sm: '48px', md: '72px' },
            }}
          >
            <Box
              component="ul"
              sx={{ ...LIST, [HOVER]: { '&:hover a:not(:hover)': { color: STAGE.mute } } }}
              onPointerLeave={(event) => point(event, null)}
            >
              {MENU.primary.map((link, i) => {
                const current = ariaCurrentFor(link, pathname, active);

                return (
                  <m.li
                    key={link.href}
                    initial={reduced ? false : { opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: OUT_EXPO, delay: i * STAGGER }}
                  >
                    <Box
                      component={RouterLink}
                      to={link.href}
                      aria-current={current}
                      onClick={(event) => onNavigate(link.href, event)}
                      onPointerEnter={(event) => point(event, link.href)}
                      onFocus={() => tab(link.href)}
                      onBlur={() => leave(link.href)}
                      sx={PRIMARY_LINK}
                    >
                      <Box component="span" aria-hidden sx={NUMBER}>
                        {ordinal(i + 1)}
                      </Box>
                      <Dot shown={marked === link.href} id={laneId} appear={trail.appear} />
                      {link.label}
                    </Box>
                  </m.li>
                );
              })}
            </Box>

            <Secondary>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr)',
                  rowGap: { xs: '40px', md: '48px' },
                }}
              >
                <div>
                  <Box component="p" id={LABEL_ID.products} sx={BLOCK_LABEL}>
                    {MENU.productsLabel}
                  </Box>
                  <Box
                    component="ul"
                    aria-labelledby={LABEL_ID.products}
                    sx={{ ...LIST, borderTop: `1px solid ${STAGE.line}` }}
                  >
                    {products.map((product) => (
                      <li key={product.id}>
                        <Box
                          component="a"
                          href={product.url}
                          onClick={(event) => onNavigate(product.url, event)}
                          sx={PRODUCT_LINK}
                        >
                          <BisqueRing size="mark" />
                          <Box component="span" sx={NUMBER}>
                            {productNumber(product.index)}
                          </Box>
                          <Box component="span" sx={NAME}>
                            {product.name}
                          </Box>
                          <span className="MenuPanel-host">
                            <Box
                              component="span"
                              sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {new URL(product.url).hostname}
                            </Box>
                            <NorthEastRounded aria-hidden className="MenuPanel-icon" />
                          </span>
                        </Box>
                      </li>
                    ))}
                    <Box component="li" sx={ROW}>
                      <BisqueRing size="mark" state="empty" />
                      <Box component="span" sx={NUMBER}>
                        {productNumber(next)}
                      </Box>
                      <Box component="span" sx={{ ...NAME, color: STAGE.mute }}>
                        {NEXT_PRODUCT.status}
                      </Box>
                    </Box>
                  </Box>
                </div>

                <div>
                  <Box component="p" id={LABEL_ID.legal} sx={BLOCK_LABEL}>
                    {MENU.legalLabel}
                  </Box>
                  <Box
                    component="ul"
                    aria-labelledby={LABEL_ID.legal}
                    sx={{ ...LIST, display: 'flex', flexWrap: 'wrap', columnGap: '28px' }}
                    onPointerLeave={(event) => point(event, null)}
                  >
                    {MENU.legal.map((link) => {
                      const current = ariaCurrentFor(link, pathname, active);

                      return (
                        <li key={link.href}>
                          <Box
                            component={RouterLink}
                            to={link.href}
                            aria-current={current}
                            onClick={(event) => onNavigate(link.href, event)}
                            onPointerEnter={(event) => point(event, link.href)}
                            onFocus={() => tab(link.href)}
                            onBlur={() => leave(link.href)}
                            sx={LEGAL_LINK}
                          >
                            <Dot shown={marked === link.href} appear={trail.appear} />
                            {link.label}
                          </Box>
                        </li>
                      );
                    })}
                  </Box>
                </div>
              </Box>
            </Secondary>
          </Box>

          <Secondary>
            <Box
              component="p"
              sx={{
                ...TYPE.fine,
                m: 0,
                py: '24px',
                borderTop: `1px solid ${STAGE.line}`,
                color: STAGE.mute,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {copyright(YEAR)}
            </Box>
          </Secondary>
        </Box>
      </Box>
    </m.div>
  );
});

export default MenuPanel;
